import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const SubmissionHistory = ({ session, onBack, onResume }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!session?.user?.id) return;
      
      const { data, error } = await supabase
        .from('growth_forms_2024')
        .select('*')
        .eq('user_id', session.user.id)
        .order('updated_at', { ascending: false });

      if (!error) setSubmissions(data);
      setLoading(false);
    };

    fetchHistory();
  }, [session]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-20">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Retrieving History...</p>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight">Your Strategy History</h2>
          <p className="text-slate-500 text-sm font-medium mt-1">Review and manage your previous growth sessions.</p>
        </div>
        <button 
          onClick={onBack}
          className="p-4 bg-white border border-slate-200 rounded-[20px] text-slate-400 hover:text-slate-900 shadow-sm transition-all"
        >
          <SafeIcon name="X" className="w-6 h-6" />
        </button>
      </div>

      {submissions.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-[40px] p-20 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-6 text-slate-300">
            <SafeIcon name="Clipboard" className="w-8 h-8" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">No Submissions Found</h3>
          <p className="text-slate-400 text-sm max-w-xs mx-auto mb-8">You haven't completed any growth strategies yet. Start your first session now.</p>
          <button 
            onClick={onBack}
            className="px-8 py-3.5 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all"
          >
            Start New Session
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {submissions.map((item) => (
            <div 
              key={item.id}
              className="bg-white border border-slate-200 rounded-[32px] p-6 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-500/5 transition-all group"
            >
              <div className="flex items-center gap-5">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${item.is_completed ? 'bg-emerald-50 text-emerald-500' : 'bg-blue-50 text-blue-500'}`}>
                  <SafeIcon name={item.is_completed ? "CheckCircle" : "Clock"} className="w-7 h-7" />
                </div>
                <div>
                  <h4 className="font-black text-slate-900 text-lg">
                    {item.form_data?.businessName || 'Unnamed Business'}
                  </h4>
                  <div className="flex items-center gap-3 text-slate-400 text-xs font-bold uppercase tracking-wider mt-1">
                    <span>{new Date(item.updated_at).toLocaleDateString()}</span>
                    <span className="w-1 h-1 bg-slate-200 rounded-full" />
                    <span className={item.is_completed ? 'text-emerald-500' : 'text-blue-500'}>
                      {item.is_completed ? 'Finalized' : `Step ${item.current_step + 1} of 9`}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => onResume(item)}
                  className="flex-1 md:flex-none px-6 py-3 bg-slate-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-blue-600 transition-all"
                >
                  {item.is_completed ? 'View Results' : 'Resume Session'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default SubmissionHistory;